import React, { useEffect, useState } from "react";
import {
    RoleEnum,
    UserSummary,
    createUser,
    getUserByEmail,
    assignRoles,
    deleteUser,
    listAssignableUsers,
    setUserPassword,
} from "../../api/UserAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import { OrgAccount, OrgStatus, listOrgAccounts, setOrganizationStatus } from "../../api/OrgAPI";

interface Props {
    token: string;
    authorities: string[];
}

function getAssignableRoles(authorities: string[]): RoleEnum[] {
    const isRoot    = authorities.includes("ROLE_ROOT");
    const isSysAdmin = authorities.includes("ROLE_SYS_ADMIN");
    if (isRoot)     return ["SYS_ADMIN", "CATEGORY_ADMIN", "ANALYST", "MARKETING"];
    if (isSysAdmin) return ["CATEGORY_ADMIN", "ANALYST", "MARKETING"];
    return [];
}

// ── shared role checkbox group ────────────────────────────────────────────────
const RoleCheckboxes: React.FC<{
    availableRoles: RoleEnum[];
    selected: RoleEnum[];
    onChange: (roles: RoleEnum[]) => void;
    disabled?: boolean;
    vertical?: boolean;
}> = ({ availableRoles, selected, onChange, disabled, vertical }) => (
    <div style={{ display: "flex", flexDirection: vertical ? "column" : "row", flexWrap: "wrap", gap: vertical ? "0.4rem" : "0.5rem 1.5rem", marginTop: "0.25rem" }}>
        {availableRoles.map((role) => (
            <label key={role} style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal" }}>
                <input
                    type="checkbox"
                    checked={selected.includes(role)}
                    disabled={disabled}
                    onChange={(e) =>
                        onChange(
                            e.target.checked
                                ? [...selected, role]
                                : selected.filter((r) => r !== role)
                        )
                    }
                />
                {role}
            </label>
        ))}
    </div>
);

// ── Create User section ───────────────────────────────────────────────────────
const CreateUserSection: React.FC<{ token: string; availableRoles: RoleEnum[] }> = ({ token, availableRoles }) => {
    const [username, setUsername] = useState("");
    const [email, setEmail]       = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword]       = useState(false);
    const [roles, setRoles]       = useState<RoleEnum[]>([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState<string | null>(null);
    const [success, setSuccess]   = useState<string | null>(null);

    const handleSubmit = async () => {
        setError(null);
        setSuccess(null);

        if (!username.trim() || !email.trim() || !password.trim()) {
            setError("Username, email, and password are required.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (roles.length === 0) {
            setError("Select at least one role.");
            return;
        }

        setLoading(true);
        try {
            const user = await createUser(token, { username: username.trim(), email: email.trim(), password, roles });
            setSuccess(`User created — ID: ${user.id}, email: ${user.email}`);
            setUsername("");
            setEmail("");
            setPassword("");
            setConfirmPassword("");
            setRoles([]);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="section-box">
            <h3 style={{ marginBottom: "1.5rem" }}>Create User</h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: 420 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Username</label>
                    <input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="e.g. john_doe"
                        disabled={loading}
                    />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="e.g. john@example.com"
                        disabled={loading}
                    />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Password</label>
                    <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        disabled={loading}
                    />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Confirm password</label>
                    <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        disabled={loading}
                    />
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal" }}>
                    <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
                    Show password
                </label>

                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Roles</label>
                    <RoleCheckboxes availableRoles={availableRoles} selected={roles} onChange={setRoles} disabled={loading} vertical />
                </div>

                <div>
                    <button onClick={handleSubmit} disabled={loading}>
                        {loading ? "Creating…" : "Create User"}
                    </button>
                </div>

                {error   && <div className="error-message">{error}</div>}
                {success && <div style={{ color: "var(--color-success, green)" }}>{success}</div>}
            </div>
        </div>
    );
};


// ── Registered accounts (ROOT / SYS_ADMIN) ────────────────────────────────────
const RegisteredAccountsSection: React.FC<{ token: string }> = ({ token }) => {
    const [accounts, setAccounts] = useState<OrgAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setAccounts(await listOrgAccounts(token));
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [token]);

    const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "—");

    return (
        <div className="section-box">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                <h3 style={{ margin: 0 }}>Registered Accounts</h3>
                <button type="button" className="btn-sm" onClick={load} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>
            <div className="item-description" style={{ marginBottom: "0.5rem" }}>
                Organizations that signed up, newest first. Use the org id below in
                the Organization Status tool.
            </div>

            {error && <div className="error-message">{error}</div>}

            {accounts.length === 0 && !loading ? (
                <p style={{ color: "var(--text-secondary)" }}>No registered accounts yet.</p>
            ) : (
                <div className="results-table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Org id</th>
                                <th>Organization</th>
                                <th>Owner (root user)</th>
                                <th>Created</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map((a) => (
                                <tr key={a.orgId}>
                                    <td>{a.orgId}</td>
                                    <td>{a.orgName ?? "—"}</td>
                                    <td>
                                        {a.rootEmail ?? "—"}
                                        {a.rootUsername && (
                                            <div style={{ color: "var(--text-secondary)", fontSize: "0.8rem" }}>{a.rootUsername}</div>
                                        )}
                                    </td>
                                    <td>{fmt(a.createdAt)}</td>
                                    <td>
                                        {a.status}
                                        {a.status !== "ACTIVE" && a.closedAt && (
                                            <div style={{ color: "var(--text-secondary)", fontSize: "0.8rem" }}>{fmt(a.closedAt)}</div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {accounts.length > 0 && (
                <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                    {accounts.length} account{accounts.length === 1 ? "" : "s"}
                </div>
            )}
        </div>
    );
};

// ── Organization status (ROOT / SYS_ADMIN) ────────────────────────────────────
const OrgStatusSection: React.FC<{ token: string }> = ({ token }) => {
    const [orgId, setOrgId] = useState("");
    const [status, setStatus] = useState<OrgStatus>("ACTIVE");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const apply = async () => {
        const id = Number(orgId);
        if (!Number.isInteger(id) || id <= 0) {
            setError("Enter a valid organization id.");
            return;
        }
        if (!window.confirm(`Set organization ${id} to ${status}?`)) return;
        setBusy(true);
        setError(null);
        setInfo(null);
        try {
            await setOrganizationStatus(token, id, status);
            setInfo(`Organization ${id} is now ${status}.`);
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="section-box">
            <h3 style={{ marginBottom: "1rem" }}>Organization Status</h3>
            <div className="item-description" style={{ marginBottom: "0.75rem" }}>
                SUSPENDED blocks the organization's logins and hides its listings;
                ACTIVE reopens a suspended or closed organization (listings return
                exactly as they were). Closing is done only by the organization's
                own root user from their Profile page.
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                <input
                    type="number"
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    placeholder="Organization id"
                    disabled={busy}
                    style={{ width: "10rem" }}
                />
                <select value={status} onChange={(e) => setStatus(e.target.value as OrgStatus)} disabled={busy}>
                    <option value="ACTIVE">ACTIVE (reopen)</option>
                    <option value="SUSPENDED">SUSPENDED</option>
                </select>
                <button onClick={apply} disabled={busy || !orgId.trim()}>
                    {busy ? "Applying…" : "Apply"}
                </button>
            </div>
            {error && <div className="error-message" style={{ marginTop: "0.5rem" }}>{error}</div>}
            {info && <div className="success-message" style={{ marginTop: "0.5rem" }}>{info}</div>}
        </div>
    );
};

const PROTECTED_ROLES = new Set(["ORG_ROOT", "STORE_INVENTORY_MANAGER"]);

// ── Delete User section ───────────────────────────────────────────────────────
const DeleteUserSection: React.FC<{ token: string }> = ({ token }) => {
    const [email, setEmail]               = useState("");
    const [userId, setUserId]             = useState<number | null>(null);
    const [foundEmail, setFoundEmail]     = useState<string | null>(null);
    const [currentRoles, setCurrentRoles] = useState<string[]>([]);
    const [confirmed, setConfirmed]       = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [lookupError, setLookupError]     = useState<string | null>(null);
    const [deleteError, setDeleteError]     = useState<string | null>(null);
    const [success, setSuccess]             = useState<string | null>(null);

    const reset = () => {
        setUserId(null);
        setFoundEmail(null);
        setCurrentRoles([]);
        setConfirmed(false);
        setDeleteError(null);
    };

    const handleLookup = async () => {
        setLookupError(null);
        setSuccess(null);
        reset();
        if (!email.trim()) return;

        setLookupLoading(true);
        try {
            const user = await getUserByEmail(token, email.trim());
            setUserId(user.id);
            setFoundEmail(user.email);
            setCurrentRoles(user.roles ?? []);
        } catch (err) {
            logApiError(err);
            setLookupError(getUserMessage(err));
        } finally {
            setLookupLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!userId) return;
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await deleteUser(token, userId);
            setSuccess(`User "${foundEmail}" has been deleted.`);
            setEmail("");
            reset();
        } catch (err) {
            logApiError(err);
            setDeleteError(getUserMessage(err));
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="section-box">
            <h3 style={{ marginBottom: "1.5rem" }}>Delete User</h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: 420 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Find user by email</label>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); reset(); }}
                            placeholder="user@example.com"
                            disabled={lookupLoading}
                            onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
                        />
                        <button type="button" onClick={handleLookup} disabled={lookupLoading || !email.trim()} className="btn-sm">
                            {lookupLoading ? "…" : "Find"}
                        </button>
                    </div>
                    {lookupError && <div className="error-message" style={{ marginTop: "0.35rem" }}>{lookupError}</div>}
                </div>

                {userId !== null && (
                    <>
                        <div style={{ fontSize: "0.9rem" }}>
                            Found: <strong>{foundEmail}</strong> (ID: {userId})
                        </div>

                        {currentRoles.length > 0 && (
                            <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                                Current roles: {currentRoles.join(", ")}
                            </div>
                        )}

                        {currentRoles.some(r => PROTECTED_ROLES.has(r)) ? (
                            <div style={{ fontSize: "0.9rem", color: "var(--color-danger, #c0392b)" }}>
                                This user has an ORG_ROOT or STORE_INVENTORY_MANAGER role and cannot be deleted.
                            </div>
                        ) : !confirmed ? (
                            <button
                                type="button"
                                onClick={() => setConfirmed(true)}
                                style={{ background: "var(--color-danger, #c0392b)", color: "#fff", border: "none", borderRadius: 4, padding: "0.45rem 1rem", cursor: "pointer" }}
                            >
                                Remove User
                            </button>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                <div style={{ fontWeight: 600, color: "var(--color-danger, #c0392b)" }}>
                                    Are you sure you want to permanently delete {foundEmail}?
                                </div>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        disabled={deleteLoading}
                                        style={{ background: "var(--color-danger, #c0392b)", color: "#fff", border: "none", borderRadius: 4, padding: "0.45rem 1rem", cursor: "pointer" }}
                                    >
                                        {deleteLoading ? "Deleting…" : "Yes, Delete"}
                                    </button>
                                    <button type="button" onClick={() => setConfirmed(false)} disabled={deleteLoading}>
                                        Cancel
                                    </button>
                                </div>
                                {deleteError && <div className="error-message">{deleteError}</div>}
                            </div>
                        )}
                    </>
                )}

                {success && <div style={{ color: "var(--color-success, green)" }}>{success}</div>}
            </div>
        </div>
    );
};

// ── Inline editor (roles + password) used from the admin list ─────────────────
const InlineUserEditor: React.FC<{
    token: string;
    user: UserSummary;
    availableRoles: RoleEnum[];
    onChanged: () => void;
    onClose: () => void;
}> = ({ token, user, availableRoles, onChanged, onClose }) => {
    const [roles, setRoles] = useState<RoleEnum[]>(
        (user.roles ?? []).filter((r): r is RoleEnum => availableRoles.includes(r as RoleEnum))
    );
    const [rolesSaving, setRolesSaving] = useState(false);
    const [rolesMsg, setRolesMsg]       = useState<string | null>(null);
    const [rolesErr, setRolesErr]       = useState<string | null>(null);

    const [newPassword, setNewPassword]         = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword]       = useState(false);
    const [pwSaving, setPwSaving] = useState(false);
    const [pwMsg, setPwMsg]       = useState<string | null>(null);
    const [pwErr, setPwErr]       = useState<string | null>(null);

    const saveRoles = async () => {
        setRolesErr(null);
        setRolesMsg(null);
        if (roles.length === 0) { setRolesErr("Select at least one role."); return; }
        setRolesSaving(true);
        try {
            await assignRoles(token, user.id, roles);
            setRolesMsg("Roles updated.");
            onChanged();
        } catch (err) {
            logApiError(err);
            setRolesErr(getUserMessage(err));
        } finally {
            setRolesSaving(false);
        }
    };

    const savePassword = async () => {
        setPwErr(null);
        setPwMsg(null);
        if (newPassword.length < 8) { setPwErr("Password must be at least 8 characters."); return; }
        if (newPassword !== confirmPassword) { setPwErr("Passwords do not match."); return; }
        setPwSaving(true);
        try {
            await setUserPassword(token, user.id, newPassword);
            setPwMsg("Password updated.");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            logApiError(err);
            setPwErr(getUserMessage(err));
        } finally {
            setPwSaving(false);
        }
    };

    return (
        <div style={{ border: "1px solid var(--border-color)", borderRadius: 6, padding: "1rem", margin: "0.5rem 0", background: "var(--bg-secondary)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <strong>Edit {user.email} (ID: {user.id})</strong>
                <button type="button" className="btn-sm btn-secondary" onClick={onClose}>Close</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", maxWidth: 420 }}>
                {/* Roles */}
                <div>
                    <div style={{ fontWeight: 600, marginBottom: "0.4rem" }}>Roles</div>
                    <div className="item-description" style={{ marginBottom: "0.4rem" }}>
                        Replaces the user's current roles on save.
                    </div>
                    <RoleCheckboxes availableRoles={availableRoles} selected={roles} onChange={setRoles} disabled={rolesSaving} vertical />
                    <div style={{ marginTop: "0.6rem" }}>
                        <button onClick={saveRoles} disabled={rolesSaving} className="btn-sm">
                            {rolesSaving ? "Saving…" : "Save Roles"}
                        </button>
                    </div>
                    {rolesErr && <div className="error-message" style={{ marginTop: "0.35rem" }}>{rolesErr}</div>}
                    {rolesMsg && <div style={{ color: "var(--color-success, green)", marginTop: "0.35rem" }}>{rolesMsg}</div>}
                </div>

                <hr style={{ border: "none", borderTop: "1px solid var(--border-color)", margin: 0 }} />

                {/* Password */}
                <div>
                    <div style={{ fontWeight: 600, marginBottom: "0.4rem" }}>Password</div>
                    <div className="form-group" style={{ marginBottom: "0.6rem" }}>
                        <label>New password</label>
                        <input
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            disabled={pwSaving}
                            style={{ width: "100%" }}
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: "0.6rem" }}>
                        <label>Confirm password</label>
                        <input
                            type={showPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter password"
                            disabled={pwSaving}
                            onKeyDown={(e) => { if (e.key === "Enter") savePassword(); }}
                            style={{ width: "100%" }}
                        />
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal", marginBottom: "0.6rem" }}>
                        <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
                        Show password
                    </label>
                    <button onClick={savePassword} disabled={pwSaving} className="btn-sm">
                        {pwSaving ? "Saving…" : "Set Password"}
                    </button>
                    {pwErr && <div className="error-message" style={{ marginTop: "0.35rem" }}>{pwErr}</div>}
                    {pwMsg && <div style={{ color: "var(--color-success, green)", marginTop: "0.35rem" }}>{pwMsg}</div>}
                </div>
            </div>
        </div>
    );
};

// ── Admin Users section ───────────────────────────────────────────────────────
type RoleFilter = "ALL" | "SYS_ADMIN" | "CATEGORY_ADMIN" | "ANALYST" | "MARKETING";

const AdminUsersSection: React.FC<{ token: string; availableRoles: RoleEnum[] }> = ({ token, availableRoles }) => {
    const [users, setUsers]     = useState<UserSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState<string | null>(null);
    const [loaded, setLoaded]   = useState(false);
    const [filter, setFilter]   = useState<RoleFilter>("ALL");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [rowError, setRowError]   = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setUsers(await listAssignableUsers(token));
            setLoaded(true);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // Load once when the section first mounts.
    useEffect(() => {
        load();
    }, [token]);

    const handleDelete = async (u: UserSummary) => {
        if (!window.confirm(`Permanently delete ${u.email}?`)) return;
        setRowError(null);
        try {
            await deleteUser(token, u.id);
            if (editingId === u.id) setEditingId(null);
            await load();
        } catch (err) {
            logApiError(err);
            setRowError(getUserMessage(err));
        }
    };

    const visible = users.filter((u) => filter === "ALL" || (u.roles ?? []).includes(filter));
    const editingUser = visible.find((u) => u.id === editingId) ?? null;

    return (
        <div className="section-box">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", gap: "1rem", flexWrap: "wrap" }}>
                <h3 style={{ margin: 0 }}>Admin Users</h3>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <select value={filter} onChange={(e) => setFilter(e.target.value as RoleFilter)} disabled={loading}>
                        <option value="ALL">All admins</option>
                        <option value="SYS_ADMIN">SYS_ADMIN</option>
                        <option value="CATEGORY_ADMIN">CATEGORY_ADMIN</option>
                        <option value="ANALYST">ANALYST</option>
                        <option value="MARKETING">MARKETING</option>
                    </select>
                    <button type="button" className="btn-sm" onClick={load} disabled={loading}>
                        {loading ? "Loading…" : "Refresh"}
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {rowError && <div className="error-message">{rowError}</div>}

            {!error && loaded && visible.length === 0 && (
                <div style={{ color: "var(--text-secondary)" }}>No matching users.</div>
            )}

            {visible.length > 0 && (
                <div className="results-table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>ID</th>
                                <th>Email</th>
                                <th>Roles</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((u, i) => (
                                <tr key={u.id}>
                                    <td>{i + 1}</td>
                                    <td>{u.id}</td>
                                    <td>{u.email}</td>
                                    <td>{(u.roles ?? []).join(", ")}</td>
                                    <td>
                                        <div style={{ display: "flex", gap: "0.4rem" }}>
                                            <button
                                                type="button"
                                                className="btn-sm btn-secondary"
                                                onClick={() => setEditingId(editingId === u.id ? null : u.id)}
                                            >
                                                {editingId === u.id ? "Editing…" : "Edit"}
                                            </button>
                                            <button type="button" className="btn-sm btn-danger" onClick={() => handleDelete(u)}>
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingUser && (
                <InlineUserEditor
                    token={token}
                    user={editingUser}
                    availableRoles={availableRoles}
                    onChanged={load}
                    onClose={() => setEditingId(null)}
                />
            )}

            {visible.length > 0 && (
                <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                    {visible.length} user{visible.length === 1 ? "" : "s"}
                </div>
            )}
        </div>
    );
};

// ── Main panel ────────────────────────────────────────────────────────────────
const UserManagementPanel: React.FC<Props> = ({ token, authorities }) => {
    const assignableRoles = getAssignableRoles(authorities);

    return (
        <div className="space-y-4">
            <h2 style={{ marginBottom: "1.5rem" }}>User Management</h2>
            <AdminUsersSection token={token} availableRoles={assignableRoles} />
            <CreateUserSection token={token} availableRoles={assignableRoles} />
            <RegisteredAccountsSection token={token} />
            <DeleteUserSection token={token} />
            <OrgStatusSection token={token} />
        </div>
    );
};

export default UserManagementPanel;
